import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import Particles, { initParticlesEngine } from '@tsparticles/react';
import { loadFull } from 'tsparticles';
import { Engine, ISourceOptions } from '@tsparticles/engine';
import { loadEmittersPlugin } from '@tsparticles/plugin-emitters';
import { loadConfettiPreset } from '@tsparticles/preset-confetti';
import { SlotMachineSymbol } from '../SlotMachineSymbol';
import CoinImageUrl from './goldRushCoin.png';
import { styleSheet } from './styles';

interface IGoldRushSymbolProps {
	symbolType: Api.SlotMachineSymbol;
	width: number;
}

export const GoldRushSymbol: React.FC<IGoldRushSymbolProps> = props => {
	const { symbolType, width } = props;
	const style: React.CSSProperties = { height: 'auto', width };
	switch (symbolType) {
		case Api.SlotMachineSymbol.Cherry: {
			style.width = (style.width as number) + 10;
			break;
		}
		case Api.SlotMachineSymbol.Tiger: {
			style.width = (style.width as number) + 5;
			break;
		}
		default: {
			break;
		}
	}
	const styleProvider = (s: React.CSSProperties) => {
		return {
			...s,
			...style,
		};
	};
	return <SlotMachineSymbol style={styleProvider} styles={[styleSheet.symbol]} type={symbolType} />;
};

/**
 * Renders animated coin particles for the Gold Rush slot machine theme.
 *
 * Uses tsparticles v3 with the confetti preset to create falling coin animations.
 * The component manages initialization state to ensure the particles engine is loaded
 * before rendering (required by v3 API).
 *
 * @param minSize - Minimum particle size in pixels (default: 30). Maximum is fixed at 68px.
 * @returns The particle system component, or null while initializing
 *
 * @see https://particles.js.org/docs/ - tsParticles v3 documentation
 * @see https://github.com/tsparticles/react - React wrapper documentation
 */
export const CoinMachine: React.FC<{ minSize?: number }> = ({ minSize = 30 }) => {
	const [init, setInit] = React.useState(false);

	React.useEffect(() => {
		// tsparticles v3 migration: The initialization pattern changed from passing an `init`
		// callback prop to the Particles component, to calling `initParticlesEngine` once
		// during component mount. This ensures the engine is ready before rendering particles.
		// See: https://github.com/tsparticles/react/blob/main/UPGRADE.md
		initParticlesEngine(async (engine: Engine) => {
			// Load the full tsparticles bundle for maximum compatibility
			// Future optimization: Consider loading only required features to reduce bundle size
			await loadFull(engine);
			// Type assertion required due to type mismatch between tsparticles Engine
			// and preset-confetti's expected engine type (likely a version compatibility issue)
			// TODO: Remove this cast once @tsparticles/preset-confetti types are updated
			await loadConfettiPreset(engine as any);
			await loadEmittersPlugin(engine);
		}).then(() => {
			setInit(true);
		});
	}, []);

	const particleOptions = React.useMemo<ISourceOptions>(
		() => ({
			autoPlay: true,
			background: {
				color: 'transparent',
			},
			detectRetina: true,
			emitters: [
				{
					direction: 'bottom',
					life: {
						count: 50,
						delay: 5,
						duration: 3,
					},
					position: {
						x: '75%',
						y: '100%',
					},
				},
				{
					direction: 'bottom',
					life: {
						count: 50,
						delay: 5,
						duration: 3,
					},
					position: {
						x: '25%',
						y: '100%',
					},
				},
			],
			fpsLimit: 30,
			particles: {
				collisions: {
					enable: true,
				},
				number: {
					density: {
						enable: true,
					},
					max: 40,
				},
				shape: {
					images: [
						{
							height: 68,
							replaceColor: false,
							src: CoinImageUrl,
							width: 154,
						},
					],
					type: 'images',
				},
				size: {
					value: { min: minSize, max: 68 },
				},
			},
			preset: 'confetti',
		}),
		[minSize]
	);

	// Don't render particles until the engine is fully initialized
	// This prevents runtime errors from the v3 API
	if (!init) {
		return null;
	}

	return (
		<div className={css(styleSheet.coinMachine)}>
			{/* Unique ID required by tsparticles v3 to manage multiple particle instances */}
			<Particles id='tsparticles-coins' options={particleOptions} />
		</div>
	);
};
